import React from "react";

import { formatSkillFrontmatterEntryLabel } from "../../utils/skill-frontmatter";

import { type FrontmatterFormState } from "./utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface FrontmatterEditFormProps {
  draftState: FrontmatterFormState;
  onDraftChange: React.Dispatch<React.SetStateAction<FrontmatterFormState>>;
  onApply: () => void;
  onCancel: () => void;
}

export const FrontmatterEditForm = ({
  draftState,
  onDraftChange,
  onApply,
  onCancel,
}: FrontmatterEditFormProps) => (
  <div className="border-t border-gray-200 px-3 py-2">
    <div className="grid gap-2.5">
      <div className="space-y-1.5">
        <div className="text-sm font-medium text-gray-700">Name</div>
        <Input
          value={draftState.name}
          onChange={event => onDraftChange(prev => ({ ...prev, name: event.target.value }))}
          placeholder="skill-name"
          className="h-9 text-sm"
        />
        <p className="text-xs text-gray-400">
          Lowercase letters, numbers, and hyphens only. Max 64 characters.
        </p>
      </div>
      <div className="space-y-1.5">
        <div className="text-sm font-medium text-gray-700">Description</div>
        <Textarea
          value={draftState.description}
          onChange={event => onDraftChange(prev => ({ ...prev, description: event.target.value }))}
          placeholder="Describe what this skill does and when to use it"
          className="min-h-[96px] text-sm"
        />
        <p className="text-xs text-gray-400">Max 1024 characters.</p>
      </div>
      <div className="space-y-1.5">
        <div className="text-sm font-medium text-gray-700">License</div>
        <Input
          value={draftState.license}
          onChange={event => onDraftChange(prev => ({ ...prev, license: event.target.value }))}
          placeholder="e.g. Apache-2.0"
          className="h-9 text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <div className="text-sm font-medium text-gray-700">Compatibility</div>
        <Input
          value={draftState.compatibility}
          onChange={event =>
            onDraftChange(prev => ({ ...prev, compatibility: event.target.value }))
          }
          placeholder="e.g. Requires Python 3.14+ and uv"
          className="h-9 text-sm"
        />
        <p className="text-xs text-gray-400">
          Environment requirements: intended product, system packages, etc.
        </p>
      </div>
      <div className="space-y-1.5">
        <div className="text-sm font-medium text-gray-700">Allowed Tools</div>
        <Input
          value={draftState.allowedTools}
          onChange={event => onDraftChange(prev => ({ ...prev, allowedTools: event.target.value }))}
          placeholder="e.g. Bash(git:*) Bash(jq:*) Read"
          className="h-9 text-sm"
        />
        <p className="text-xs text-gray-400">
          Space-delimited list of pre-approved tools. Experimental.
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300"
          checked={draftState.manualOnlyInvocation}
          onChange={event =>
            onDraftChange(prev => ({ ...prev, manualOnlyInvocation: event.target.checked }))
          }
        />
        <span>Manual-only invocation</span>
      </label>
      {draftState.extraEntries.map(entry => (
        <div key={entry.key} className="space-y-1.5">
          <div className="text-sm font-medium text-gray-700">
            {formatSkillFrontmatterEntryLabel(entry.key)}
          </div>
          <Input
            value={entry.value}
            onChange={event =>
              onDraftChange(prev => ({
                ...prev,
                extraEntries: prev.extraEntries.map(e =>
                  e.key === entry.key ? { ...e, value: event.target.value } : e,
                ),
              }))
            }
            className="h-9 text-sm"
          />
        </div>
      ))}
    </div>
    <div className="mt-4 flex justify-end gap-2">
      <Button type="button" size="xs" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="button" size="xs" onClick={onApply}>
        Apply
      </Button>
    </div>
  </div>
);
